'use client'

import AdCardWrapper from '@/components/_cards/ad/ad-card-wrapper'
import EventCard from '@/components/_cards/event/event-card'
import Loader from '@/components/common/loader'
import Filter from '@/components/forms/filter'
import Pager from '@/components/search/pager'
import SearchElement from '@/components/search/search-element'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { dayOptions } from '@/helpers/days'
import { cn } from '@/lib/utils'
import { ChevronDown, SearchIcon } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import {
  buildSearchParamsFromFilter,
  combineEventsWithAds,
  getFilterFromSearchParams,
  resetUnavailableCarClasses,
} from './racing-event-search-helpers'

const MAX_EVENTS = 24
const RACE_TYPES = ['Any', 'Single Event', 'Championship Event']
const SORT_OPTIONS = ['Any', 'Live', 'Upcoming', 'Finished']

function toUrlSearchParams(searchParams) {
  const params = new globalThis.URLSearchParams()

  Object.entries(searchParams || {}).forEach(([key, value]) => {
    if (value == null) return
    params.set(key, String(value))
  })

  return params
}

// TODO optimisation use platforms from game fetch rather than fetching platforms each time
// TODO Removable filter boxes
// TODO Support for forms
export default function RacingEventSearch({
  searchParams,
  sessionUserId,
  initialResults = null,
  initialAds = [],
  initialAttendanceByEventId = {},
  games: initialGames = [],
  platforms: initialPlatforms = [],
}) {
  const pathname = usePathname()
  const router = useRouter()

  const [pageNum, setPageNum] = useState(
    searchParams.page ? Number(searchParams.page) : 1,
  )
  const [filter, setFilter] = useState(getFilterFromSearchParams(searchParams))

  const [results, setResults] = useState(initialResults || {})
  const [ads, setAds] = useState(initialAds || [])
  const [isSearching, setIsSearching] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const allGames = initialGames
  const allPlatforms = initialPlatforms
  const gamesLoading = false
  const platformsLoading = false
  const platforms = useMemo(() => {
    if (!Array.isArray(allPlatforms)) return []
    if (!filter.game || filter.game === 'Any') return allPlatforms

    const selectedGame = (allGames || []).find(
      (game) => game.name === filter.game,
    )
    if (!selectedGame?.platforms) return allPlatforms

    const allowedPlatformNames = new Set(
      selectedGame.platforms.map((platform) => platform.platformName),
    )

    return allPlatforms.filter((platform) =>
      allowedPlatformNames.has(platform.name),
    )
  }, [allPlatforms, allGames, filter.game])
  const games = useMemo(() => {
    if (
      !Array.isArray(allGames) ||
      !filter.platforms ||
      filter.platforms.length === 0 ||
      filter.platforms[0] === 'Any'
    ) {
      return allGames || []
    }

    return allGames.filter((game) =>
      (game.platforms || []).some((platform) =>
        filter.platforms.includes(platform.platformName),
      ),
    )
  }, [allGames, filter.platforms])

  //for single choice
  function setFilterByKey(key, newValue) {
    setFilter((previous) => ({
      ...previous,
      [key]: previous[key] === newValue ? 'Any' : newValue,
    }))
  }

  //for multi choice
  function updateFilter(key, newValue) {
    setFilter((previous) => {
      if (newValue === 'Any') return { ...previous, [key]: ['Any'] }

      const current = Array.isArray(previous[key]) ? previous[key] : []
      const toggled = current.includes(newValue)
        ? current.filter((value) => value !== newValue)
        : [...current, newValue]
      const withoutAny = toggled.filter((value) => value !== 'Any')

      return {
        ...previous,
        [key]: withoutAny.length > 0 ? withoutAny : ['Any'],
      }
    })
  }

  function updateFilterSpecificallyFromFilterComponent(key, newValue) {
    setFilter((previous) => ({
      ...previous,
      [key]:
        !newValue?.length || newValue.at(-1) === 'Any'
          ? ['Any']
          : newValue.filter((value) => value !== 'Any'),
    }))
  }

  async function handleSearchClicked() {
    setPageNum(1)
    setIsSearching(true)
    const params = buildSearchParamsFromFilter(filter, 1)
    router.push(`${pathname}?${params.toString()}`)
  }

  function submitSearchOnEnterKeyPress(event) {
    if (event.key === 'Enter') {
      handleSearchClicked()
    }
  }

  const handlePageChange = (newPageNum) => {
    setPageNum(newPageNum)
    setIsSearching(true)
    const params = toUrlSearchParams(searchParams)
    params.delete('ts')
    params.set('page', String(newPageNum))
    router.push(`${pathname}?${params.toString()}`)
  }

  useEffect(() => {
    setPageNum(searchParams.page ? Number(searchParams.page) : 1)
    setFilter(getFilterFromSearchParams(searchParams))
    setResults(initialResults || {})
    setAds(initialAds || [])
    setIsSearching(false)
  }, [searchParams, initialResults, initialAds])

  // Reset platforms if selected platform does not exist for current game
  useEffect(() => {
    if (platforms) {
      setFilter((previous) => {
        let shouldResetPlatforms = false
        if (previous.platforms?.length > 0 && previous.platforms[0] !== 'Any') {
          for (const platform of previous.platforms) {
            if (platforms.find((p) => p.name === platform) === undefined) {
              shouldResetPlatforms = true
              break
            }
          }
        }

        if (!shouldResetPlatforms) return previous

        return {
          ...previous,
          platforms: ['Any'],
        }
      })
    }
  }, [platforms])

  // Filter games based on selected platforms and reset game if it's not compatible with platforms
  useEffect(() => {
    if (!platforms || !games || games.length === 0) return

    setFilter((previous) => {
      if (
        !previous.platforms?.length ||
        previous.platforms[0] === 'Any' ||
        !previous.game ||
        previous.game === 'Any'
      ) {
        return previous
      }

      const selectedGame = games.find((game) => game.name === previous.game)
      const isGameCompatible = selectedGame?.platforms?.some((platform) =>
        previous.platforms.includes(platform.platformName),
      )

      if (isGameCompatible) return previous

      return {
        ...previous,
        game: 'Any',
        carClasses: ['Any'],
      }
    })
  }, [filter.platforms, games, platforms])

  // Reset car classes if selected game changes
  useEffect(() => {
    if (!games || games.length === 0) return

    setFilter((previous) => resetUnavailableCarClasses(previous, games))
  }, [filter.game, games])

  return (
    <>
      <div className="screen-grid bg-linear-to-b col-span-full grid rounded-b-xl from-transparent to-[#08111B] pb-8 font-inter desktop-sm:pb-9">
        <p className="col-span-full mt-[29px] text-h-2-sm font-bold desktop-sm:col-start-2">
          Find your <span className="gf-gradient-text">next race</span>
        </p>

        <div className="col-span-full grid grid-cols-12 desktop-sm:col-start-2 desktop-sm:gap-x-8">
          {/* Platform dropdown */}
          <div className="col-span-full mt-5 flex flex-col desktop-sm:col-span-2">
            <p className="text-p-2 font-semibold text-neutral-blue-200">
              Platform
            </p>
            {platformsLoading || gamesLoading ? (
              <Skeleton className="mt-1 h-[48px] w-full rounded-md bg-shades-0 desktop-xs:h-[57px] desktop-sm:mt-3" />
            ) : (
              <div className="mt-1 w-full desktop-sm:mt-3">
                <SearchElement
                  value={filter['platforms']}
                  setValue={(newValue) => updateFilter('platforms', newValue)}
                  options={[{ name: 'Any', nicename: 'Any' }, ...platforms]}
                  labelKey="nicename"
                  valueKey="name"
                  isMulti
                />
              </div>
            )}
          </div>

          {/* Game dropdown */}
          <div className="col-span-full mt-3 flex flex-col desktop-sm:col-span-2 desktop-sm:mt-5">
            <p className="text-p-2 font-semibold text-neutral-blue-200">Game</p>
            {platformsLoading || gamesLoading ? (
              <Skeleton className="mt-1 h-[48px] w-full rounded-md bg-shades-0 desktop-xs:h-[57px] desktop-sm:mt-3" />
            ) : (
              <div className="mt-1 w-full desktop-sm:mt-3">
                <SearchElement
                  value={filter['game']}
                  setValue={(newValue) => setFilterByKey('game', newValue)}
                  options={[
                    { name: 'Any', nicename: 'Any', featured: true },
                    ...games,
                  ]}
                  labelKey="nicename"
                  valueKey="name"
                  grouped={true}
                  groups={[
                    { prop: 'id', compareVal: 'any', label: false },
                    { prop: 'featured', compareVal: true, label: 'Popular' },
                    {
                      prop: 'featured',
                      compareVal: false,
                      label: 'Other Titles',
                    },
                  ]}
                />
              </div>
            )}
          </div>

          {/* Race Days dropdown */}
          <div className="col-span-full mt-3 flex flex-col desktop-sm:col-span-2 desktop-sm:mt-5">
            <p className="text-p-2 font-semibold text-neutral-blue-200">
              Race Days
            </p>
            {gamesLoading || platformsLoading ? (
              <Skeleton className="mt-1 h-[48px] w-full rounded-md bg-shades-0 desktop-xs:h-[57px] desktop-sm:mt-3" />
            ) : (
              <div className="mt-1 w-full desktop-sm:mt-3">
                <SearchElement
                  value={filter['days']}
                  setValue={(newValue) => updateFilter('days', newValue)}
                  options={[{ name: 'Any', nicename: 'Any' }, ...dayOptions]}
                  labelKey="nicename"
                  valueKey="name"
                  isMulti
                />
              </div>
            )}
          </div>

          {/* Keyword search */}
          <div className="col-span-full mt-3 flex flex-col desktop-sm:col-span-3 desktop-sm:mt-5">
            <p className="text-p-2 font-semibold text-neutral-blue-200">
              Keyword
            </p>
            <Input
              className="text-md bg-shades-0! px-3! text-neutral-blue-900! placeholder-neutral-blue-500! hover:bg-[#dbdcdd]! focus:bg-shades-0! mt-1 h-[48px] w-full rounded-md border border-neutral-blue-500 text-p-2 font-semibold transition-all duration-300 ease-in-out desktop-xs:h-[57px] desktop-sm:mt-3"
              placeholder="Search communities, tracks etc..."
              onKeyUp={submitSearchOnEnterKeyPress}
              value={filter['keyword']}
              onChange={(e) => setFilterByKey('keyword', e.target.value)}
              disabled={isSearching}
            />
          </div>

          <div className="col-span-2 mb-0 mt-5 hidden w-full flex-1 items-end desktop-sm:flex">
            <Button
              disabled={isSearching}
              onClick={handleSearchClicked}
              className="bg-gf-gradient text-shades-0! h-[57px] w-full gap-1 rounded-md border border-primary-300 p-0 font-inter text-p-3 font-semibold transition-all duration-300 ease-in-out hover:opacity-85 desktop-sm:col-span-1"
            >
              {isSearching ? (
                <Loader
                  size="sm"
                  spinnerFillColor="shades-0"
                  spinnerBgColor="primary"
                />
              ) : (
                <>
                  <SearchIcon className="size-6 stroke-shades-0" />
                  <p className="text-white! text-p-3 transition-all duration-300 ease-in-out">
                    Search
                  </p>
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="col-span-full mt-6 flex flex-col gap-5 desktop-sm:col-span-10 desktop-sm:col-start-2">
          <button
            type="button"
            className="flex w-full items-center justify-center gap-1 desktop-sm:w-auto desktop-sm:justify-normal"
            onClick={() => setAdvancedOpen(!advancedOpen)}
            aria-controls="advanced-event-filters"
            aria-expanded={advancedOpen}
          >
            <p className="stroke-neutral-blue-200 text-neutral-blue-200 underline underline-offset-2">
              Advanced Filters
            </p>
            <ChevronDown
              className={cn(
                'size-5 stroke-neutral-blue-200 transition-transform duration-200',
                {
                  'rotate-180': advancedOpen,
                },
              )}
            />
          </button>

          <div
            id="advanced-event-filters"
            className={cn('flex flex-col gap-5', {
              hidden: !advancedOpen,
            })}
          >
            <div className="divide bg-neutral-blue-700" />
            <div className="flex flex-col gap-3 desktop-sm:flex-row">
              <Filter
                value={filter['type']}
                setValue={(newValue) => setFilterByKey('type', newValue)}
                options={RACE_TYPES}
                description="Race Types"
                parentClassName="desktop-sm:w-[190px] rounded-md"
                displayClassName="desktop-sm:w-[200px]"
              />
              {filter['game'] &&
                filter['game'] !== 'Any' &&
                games &&
                games.find((g) => g.name == filter['game'])?.carClasses
                  ?.length > 1 && (
                  <Filter
                    value={filter['carClasses']}
                    setValue={(newValue) =>
                      updateFilterSpecificallyFromFilterComponent(
                        'carClasses',
                        newValue,
                      )
                    }
                    options={[
                      { name: 'Any', nicename: 'Any' },
                      ...(games.find((g) => g.name == filter['game'])
                        ?.carClasses || []),
                    ]}
                    displayLabel="name"
                    valueLabel="name"
                    description="Car Classes"
                    parentClassName="desktop-sm:w-[172px] rounded-md"
                    displayClassName="desktop-sm:w-[360px]"
                    multi
                  />
                )}
              <Filter
                value={filter['sort']}
                setValue={(newValue) => {
                  if (newValue !== filter['sort'])
                    setFilterByKey('sort', newValue)
                }}
                options={SORT_OPTIONS}
                parentClassName="desktop-sm:w-[150px] rounded-md"
                displayClassName="desktop-sm:w-[200px]"
              />
            </div>
          </div>

          <div className="col-span-2 mb-0 mt-1 flex w-full flex-1 items-end desktop-sm:hidden">
            <Button
              disabled={isSearching}
              onClick={handleSearchClicked}
              className="bg-gf-gradient text-shades-0! h-[57px] w-full gap-1 rounded-md border border-primary-300 p-0 font-inter text-p-3 font-semibold transition-all duration-300 ease-in-out hover:opacity-85 desktop-sm:col-span-1"
            >
              {isSearching ? (
                <Loader
                  size="sm"
                  spinnerFillColor="shades-0"
                  spinnerBgColor="primary"
                />
              ) : (
                <>
                  <SearchIcon className="size-6 min-w-6 stroke-shades-0" />
                  <p className="text-white! text-p-3 transition-all duration-300 ease-in-out">
                    Search
                  </p>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="screen-grid col-span-full mt-6 desktop-sm:mt-8">
        <div className="col-span-full grid gap-5 tablet:grid-cols-2 desktop-sm:col-span-10 desktop-sm:col-start-2 desktop-sm:grid-cols-3 desktop-lg:gap-9">
          {isSearching ? (
            Array(MAX_EVENTS)
              .fill()
              .map((_, i) => <Skeleton key={i} className="aspect-2/3 w-full" />)
          ) : results.events && results.events?.length === 0 ? (
            <Link
              href="/sim-racing-communities"
              className="mt-2 font-inter text-p-2 font-semibold text-neutral-blue-200 underline underline-offset-2"
            >
              Looking to search communities instead?
            </Link>
          ) : (
            combineEventsWithAds(results.events, ads).map((item) => (
              <div key={item.key}>
                {item.type === 'event' ? (
                  <EventCard
                    championship={item.data.championship}
                    event={item.data}
                    initialAttendance={
                      initialAttendanceByEventId?.[item.data.id]
                    }
                    disablePrefetch
                    lazyAttendance
                    showRegistrationCountdownOnly
                    session={
                      sessionUserId ? { user: { id: sessionUserId } } : null
                    }
                  />
                ) : (
                  <AdCardWrapper ad={item.data} placement={item.placement} />
                )}
              </div>
            ))
          )}
        </div>

        {!isSearching && results.events?.length > 0 && (
          <Pager
            className="col-span-full mt-10 desktop-sm:col-span-10 desktop-sm:col-start-2 desktop-sm:max-w-[408px] desktop-sm:justify-self-end"
            page={pageNum}
            pageLimit={Math.ceil(results.total / MAX_EVENTS)}
            setPage={handlePageChange}
          />
        )}
      </div>
    </>
  )
}
